import { useState } from 'react';
import { useT } from '@/store';

/**
 * A credential the server returns ONCE, with the button that gets it off the
 * screen and into a password manager.
 *
 * V-364. This markup and its clipboard handler existed twice — inside
 * DriverKeyModal as a module-local `Secret`, and hand-rolled again in
 * ClientAccessModal — which is exactly why the third caller had nothing to
 * call. ApiKeyPanel, which shows the most powerful credential in the product
 * (its own docstring: "a full-access bearer token"), rendered it in a bare
 * <code> block with no copy button at all.
 *
 * That asymmetry is the defect. Both siblings say the rule out loud —
 * ClientAccessModal: "this is a one-time credential the server returns once
 * and never again, so it gets a modal the admin dismisses deliberately with
 * an explicit copy button" — and the one surface holding the strongest
 * secret was the one that skipped it. blueprints/tenants.py stores only
 * sha256 of that key, so a copy that comes out short cannot be checked
 * against anything or read a second time: the only way back is another
 * rotation, and if "revoke document links" was ticked, that breaks every
 * delivery-note link a client is holding for a second time.
 *
 * OWNS ITS OWN `copied` FLASH, unlike the version this replaces. That one
 * took `copied`/`onCopy` from its parent so DriverKeyModal could track which
 * of three fields had been pressed; per-field state answers the same
 * question without the parent knowing there are three. Modal unmounts its
 * children when it closes (Overlay.jsx: `if (!open) return null`), so the
 * flash resets on its own and no caller has to remember to clear it.
 *
 * The catch around the clipboard write is deliberate and stays: it needs a
 * secure context, and where there isn't one the value is still on screen to
 * be selected by hand — which is the state ApiKeyPanel was permanently in.
 */
export function SecretField({ label, value }) {
  const t = useT();
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2500);
    } catch {
      // Clipboard needs a secure context; the value is on screen either way.
    }
  };

  return (
    <div>
      {label ? (
        <span className="mb-1 block text-[10px] font-bold uppercase tracking-[0.03em] text-dim">
          {label}
        </span>
      ) : null}
      <div className="flex gap-2">
        <code className="flex-1 break-all rounded-[9px] border border-line bg-white/4 px-3 py-2 text-[12px]">
          {value}
        </code>
        <button type="button" className="btn" onClick={copy}>
          {copied ? t('common.addedCheck') : t('drivers.copyKey')}
        </button>
      </div>
    </div>
  );
}
